// Route Code Edited 8/10/2026
/**
 * Route Element Text Handler (Zippy)
 *
 * Watches for configured elements inside <route-cart-preferred>'s shadow DOM
 * and enforces text content via MutationObserver. Re-enforces on any mutation
 * to prevent third-party apps -- or the widget's own re-renders -- from
 * overwriting Route's element text.
 *
 * Each entry in ELEMENT_CONFIGS targets a selector independently.
 *
 * Note: by default processText replaces the element's entire textContent,
 * removing all child nodes. Set `preserveChildren: true` on a config to
 * instead replace only the element's first non-empty direct text node,
 * leaving element children (eg. a <span>) untouched. Verify the target
 * element's DOM structure before use either way.
 *
 * Zippy renders <route-cart-preferred> as an open shadow host, so this
 * script queries `host.shadowRoot` directly from the main document --
 * no injection into the shadow root itself is needed or possible.
 *
 * Installation:
 * Add this script tag at the bottom of layout/theme.liquid (before </body>):
 *
 * <!-- Route Code Edited 8/10/2026 -->
 * <script src="{{ 'route-element-text-handler.js' | asset_url }}" defer></script>
 * <!-- End Route Code -->
 *
 */
(function routeElementTextHandler() {
  // ─── Configure below ──────────────────────────────────────
  const WIDGET_HOST_TAG = "route-cart-preferred";
  const ELEMENT_CONFIGS = [
    {
      selector: "[part='card-title']",
      textMatchRegex: /Your order is protected\./, // RegExp — if set, processText only runs when text matches
      processText: () => 'Your order is protected by Route.', // function(currentText) => string, eg. () => 'Checkout',
      preserveChildren: true, // true = only replace the first non-empty direct text node
    },
  ];
  // ────────────────────────────────────────────────────
  function getTargetNode(el, config) {
    if (!config.preserveChildren) return el;
    return (
      Array.from(el.childNodes).find(
        (node) =>
          node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== "",
      ) || el
    );
  }
  function shouldProcessText(el, config) {
    if (config.processText == null) return false;
    const text = getTargetNode(el, config).textContent.trim();
    if (config.textMatchRegex != null && !config.textMatchRegex.test(text))
      return false;
    return true;
  }
  function hasUnwantedState(el, config) {
    if (!shouldProcessText(el, config)) return false;
    const text = getTargetNode(el, config).textContent.trim();
    return text !== config.processText(text);
  }
  function applyChanges(el, config, observer) {
    observer.disconnect();
    if (shouldProcessText(el, config)) {
      const node = getTargetNode(el, config);
      const newText = config.processText(node.textContent.trim());
      if (node.nodeType === Node.TEXT_NODE) node.nodeValue = newText;
      else node.textContent = newText;
    }
    observer.observe(el, {
      characterData: true,
      subtree: true,
    });
  }
  function watchElement(el, config) {
    if (el._routeHandlerWatched) return;
    el._routeHandlerWatched = true;
    const observer = new MutationObserver(() => {
      if (hasUnwantedState(el, config)) applyChanges(el, config, observer);
    });
    applyChanges(el, config, observer);
  }
  function scanShadowRoot(shadowRoot) {
    ELEMENT_CONFIGS.forEach((config) => {
      shadowRoot
        .querySelectorAll(config.selector)
        .forEach((el) => watchElement(el, config));
    });
  }
  function watchHost(host) {
    if (host._routeShadowWatched) return;
    // Lit attaches the shadow root in the constructor, which always runs
    // before an upgraded custom element is observable in the DOM tree.
    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) return;
    host._routeShadowWatched = true;
    const shadowObserver = new MutationObserver(() => scanShadowRoot(shadowRoot));
    shadowObserver.observe(shadowRoot, { childList: true, subtree: true });
    scanShadowRoot(shadowRoot);
    window.addEventListener("pagehide", () => shadowObserver.disconnect());
  }
  function scanForHosts() {
    document.querySelectorAll(WIDGET_HOST_TAG).forEach(watchHost);
  }
  const domObserver = new MutationObserver(scanForHosts);
  domObserver.observe(document.body, { childList: true, subtree: true });
  scanForHosts();
  window.addEventListener("pagehide", () => domObserver.disconnect());
})();
